import React, { useState, Fragment, useContext } from 'react';
import { Form, Formik } from 'formik';
import { Cell } from 'react-foundation';
import classnames from 'classnames';
import * as Yup from 'yup';

import styles from './InviteParticipantsPage.module.scss';

import PrimaryButton from '../../../components/common/buttons/PrimaryButton';
import Loading from '../../../components/common/Loading';
import FieldInput from '../../../components/common/fields/FieldInput/FieldInput';
import MessageModal from '../../../components/MessageModal';
import ResearcherLeftNav from '../../../components/nav/ResearcherLeftNav';

import AuthContext from '../../../context/AuthContext';
// import ClinicContext from '../../../context/ClinicContext';

import messages from './messages';
import useIntl from '../../../hooks/useIntl';

import { inviteParticipant } from '../../../api/participant';

const tabs = ['EMAIL', 'SMS'];

const InviteParticipantsPage = () => {
  const { formatMessage } = useIntl();

  const { authUser } = useContext(AuthContext);
  // const { clinic } = useContext(ClinicContext);

  const [error, setError] = useState('');
  const [dataLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [displayMessage, setDisplayMessage] = useState({
    title: '',
    message: '',
  });

  const EmailSchema = Yup.object().shape({
    firstName: Yup.string().required('Field Required'),
    email: Yup.string().email().required('Field Required'),
  });
  const SmsSchema = Yup.object().shape({
    firstName: Yup.string().required('Field Required'),
    phone: Yup.string()
      .min(10, 'Please enter valid phone number')
      .max(10, 'Please enter valid phone number')
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        'Please enter valid phone number',
      )
      .required('Phone number is required.'),
  });

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const { email, firstName, phone } = values;
      const { id } = authUser;

      await inviteParticipant({
        email: email === '' ? null : email,
        firstName,
        researcher_id: id,
        invited_status: 'pending',
        lab_id: authUser.lab_id,
        phone: phone === '' ? null : phone,
      });

      setLoading(false);
      setDisplayMessage({
        type: 'success',
        title: formatMessage(messages.modalSuccessText),
        text: '',
      });

      setShowMessageModal(true);
    } catch (error) {
      let errorMsg = '';
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMsg = error.response.data.message;
      }
      setError(errorMsg);
      setLoading(false);
      setDisplayMessage({
        type: 'error',
        title: '',
        text: errorMsg || formatMessage(messages.modalFailureText),
      });
      setShowMessageModal(true);
      throw error;
    }
  };

  const onclickTab = (tab) => {
    setActiveTab(tab);
  };

  return (
    <>
      <div className={styles.container}>
        <div medium={3} className={classnames(styles.leftnav, 'desktop-only')}>
          <ResearcherLeftNav />
        </div>
        {dataLoading ? (
          <Loading />
        ) : (
          <Fragment>
            <div className={styles.lineArt}>
              <img src={require(`../../../img/LineArt.svg`)} alt="line-art" />
            </div>
            <div className={styles.contentDiv}>
              <div
                medium={3}
                className={classnames(styles.toptnav, 'mobile-only')}
              ></div>

              <Formik
                initialValues={{ email: '', firstName: '', phone: '' }}
                validationSchema={
                  activeTab === 'EMAIL' ? EmailSchema : SmsSchema
                }
                onSubmit={async (values) => {
                  handleSubmit(values);
                }}
              >
                {({ errors, values, handleBlur, setFieldValue, isValid }) => {
                  return (
                    <Form className={styles['content']}>
                      <div className={classnames(styles[`form-div`])}>
                        <h2 data-testid="invite-participants header">
                          Invite Participants
                        </h2>
                        <div
                          className={styles['sub-text']}
                          data-testid="invite-participants subtext"
                        >
                          Invite Participants To Join Eve Health Systems
                          {/*clinic && clinic.clinic_name
                            ? clinic.clinic_name
                            : 'Eve Health Systems'*/}{' '}
                          Platform
                        </div>
                        <nav className={styles.tabs}>
                          <div>
                            <p data-testid="participants send_invitation">
                              How do you want to send your invitation?
                            </p>
                          </div>
                          <div>
                            {tabs.map((tab) => (
                              <button
                                key={tab}
                                className={[
                                  styles.tab,
                                  activeTab === tab ? styles.activeTab : '',
                                ].join(' ')}
                                type="button"
                                onClick={() => {
                                  setFieldValue('email', '');
                                  setFieldValue('phone', '');
                                  onclickTab(tab);
                                }}
                              >
                                <span data-testid={tab}>{tab}</span>
                              </button>
                            ))}
                          </div>
                        </nav>

                        <div className={styles.formInput}>
                          <Cell small={12} medium={12}>
                            <FieldInput
                              label="First Name"
                              className={styles.input}
                              name="firstName"
                              errorText={errors.firstName}
                              onChange={(e) => {
                                setFieldValue('firstName', e.target.value);
                              }}
                              onBlur={handleBlur}
                              value={values.firstName}
                              placeholder="First Name"
                              data-testid="participants first_name"
                              label-testid="participants first_name label"
                              error-testid="participants first_name error"
                            />
                          </Cell>
                          <Cell small={12} medium={12}>
                            {activeTab === 'EMAIL' ? (
                              <FieldInput
                                label="email"
                                className={styles.input}
                                name="email"
                                errorText={errors.email}
                                onChange={(e) => {
                                  setError('');
                                  setFieldValue('email', e.target.value);
                                }}
                                onBlur={handleBlur}
                                value={values.email}
                                placeholder="Participants's Email Address"
                                data-testid="participants email"
                                label-testid="participants email label"
                                error-testid="participants email error"
                              />
                            ) : (
                              <>
                                <FieldInput
                                  label="Mobile number"
                                  className={styles.input}
                                  name="phone"
                                  errorText={errors.phone}
                                  onChange={(e) => {
                                    setError('');
                                    setFieldValue('phone', e.target.value);
                                  }}
                                  onBlur={handleBlur}
                                  value={values.phone}
                                  placeholder="Mobile number"
                                  data-testid="participants phone"
                                  label-testid="participants phone label"
                                  error-testid="participants phone error"
                                />
                              </>
                            )}
                          </Cell>
                        </div>
                        {error && (
                          <div
                            className={styles.error}
                            data-testid="invite_patient error"
                          >
                            {error}
                          </div>
                        )}
                        <div className={classnames(styles[`button-row`])}>
                          <PrimaryButton
                            label="Invite"
                            className={classnames(styles[`button`])}
                            disabled={!isValid}
                            data-testid="participant invite button"
                          />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Fragment>
        )}
      </div>

      {showMessageModal && (
        <MessageModal
          message={displayMessage}
          onCloseModal={() => setShowMessageModal(false)}
          redirectUrl={'/doctor/my-account'}
        />
      )}
    </>
  );
};

export default InviteParticipantsPage;
